import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { Box } from '@mui/material';
import CardTemplate from './CardTemplate';
import { useChartTheme } from '../overview/plotTheme';

// Same colours on every chart so energy vs. peak power reads the same everywhere
const DEFAULT_ENERGY_COLOR = "#10B981"; // Same green as grid export
const DEFAULT_POWER_COLOR = "#F97316";

const PlotCard = ({
  cardTitle,
  cardSubtitle,
  seriesEnergy,
  seriesPeakPower,
  energyColor = DEFAULT_ENERGY_COLOR,
  powerColor = DEFAULT_POWER_COLOR,
  binMs = 3600 * 1000, // Duration of one bar (hour / day / month)
  windowMs = 24 * binMs, // Time span shown when there is no data
  xTickFormat, // Optional: one tick per bar with this format (e.g. "%b %d"), otherwise Plotly picks the ticks
}) => {

  const { mode, colors } = useChartTheme();

  useEffect(() => {
    let _data = [];
    // Timestamps are timezone-naive "YYYY-MM-DD HH:mm:ss", parse as UTC to match Plotly
    const toMs = (x) => typeof x === "number" ? x : Date.parse(String(x).replace(" ", "T") + "Z");
    // Back to a naive string, Plotly applies a timezone shift to numeric ms on date axes
    const toStr = (ms) => new Date(ms).toISOString().slice(0, 23).replace("T", " ");

    if (seriesEnergy && seriesEnergy.length > 0 && seriesEnergy[0].x && seriesEnergy[0].x.length > 0) {

      seriesEnergy.map((dataset, index) => {
        let _name = "Energy production " + (index + 1);
        let _showLegend = false;
        if (index == 0) {
          _name = "Energy production";
          _showLegend = true;
        }
        _data.push({
          x: dataset.x,
          y: dataset.y,
          type: "bar",
          // mode: "none",
          name: _name,
          marker: { color: energyColor, cornerradius: 3 },
          width: binMs * 0.8, // Fixed width so gaps or a single point don't distort the bars
          offset: binMs * 0.1, // Timestamps mark the interval start, so draw the bar inside [t, t + binMs]
          hovertemplate: "%{x}<br>%{y:,.1f} kWh<extra></extra>",
          // stackgroup: "energy",
          showlegend: _showLegend,
          // hoverinfo: "none",
        })

        // Plotly doesn't draw zero-height bars, so mark zero values with a thin line at y = 0
        const _zeroX = [];
        const _zeroY = [];
        dataset.x.forEach((x, i) => {
          const _t = toMs(x);
          if (dataset.y[i] === 0 && !isNaN(_t)) {
            _zeroX.push(toStr(_t + binMs * 0.1), toStr(_t + binMs * 0.9), null);
            _zeroY.push(0, 0, null);
          }
        });
        if (_zeroX.length > 0) {
          _data.push({
            x: _zeroX,
            y: _zeroY,
            type: "scatter",
            mode: "lines",
            line: { color: energyColor, width: 4 },
            showlegend: false,
            hoverinfo: "skip",
          });
        }
      });
    }
    
    // console.log(seriesPeakPower);
    if (seriesPeakPower && seriesPeakPower.length && seriesPeakPower[0].x && seriesPeakPower[0].x.length > 0) {

      seriesPeakPower.map((dataset, index) => {
        let _name = "Peak power " + (index + 1);
        let _showLegend = false;
        if (index == 0) {
          _name = "Peak power";
          _showLegend = true;
        }
        _data.push({
          // Timestamps mark the interval start, shift to the middle so points line up with the energy bars
          x: dataset.x.map(x => { const _t = toMs(x); return isNaN(_t) ? x : toStr(_t + binMs * 0.5); }),
          y: dataset.y,
          text: dataset.x, // Show the original interval timestamp on hover
          hovertemplate: "%{text}<br>%{y:,.1f} kW<extra></extra>",
          type: "scatter",
          mode: "lines+markers",
          name: _name,
          yaxis: "y2",
          line: { color: powerColor, width: 2.5 },
          marker: { color: powerColor, size: 5, line: { color: colors.markerOutline, width: 1 } },
          fill: "none",
          // line: { shape: "spline" },
          // stackgroup: "power",
          showlegend: _showLegend,
          // hoverinfo: "none",
        })
      });

    }

    // With a single timestamp Plotly zooms the date axis to sub-milliseconds, so pad the range manually
    const _times = _data.flatMap(trace => trace.x.filter(x => x !== null).map(toMs)).filter(t => !isNaN(t));
    const _tMin = Math.min(..._times);
    const _tMax = Math.max(..._times);
    if (_times.length === 0) {
      // No data, show an empty grid over the window ending now (local wall-clock time, same as the data)
      const _now = Date.now() - new Date().getTimezoneOffset() * 60 * 1000;
      setXRange([toStr(_now - windowMs), toStr(_now)]);
      // Placeholder traces (single null point) so the legend and right axis still render
      _data = [
        { x: [toStr(_now)], y: [null], type: "bar", name: "Energy production", marker: { color: energyColor }, showlegend: true },
        { x: [toStr(_now)], y: [null], type: "scatter", mode: "lines+markers", name: "Peak power", yaxis: "y2", line: { color: powerColor }, showlegend: true },
      ];
    } else {
      setXRange(_tMax - _tMin < binMs ? [toStr(_tMin - 3 * binMs), toStr(_tMax + 3 * binMs)] : undefined);
    }

    setIsEmpty(_times.length === 0);

    setData(_data);
  }, [seriesEnergy, seriesPeakPower, binMs, windowMs, energyColor, powerColor, mode]);

  const [data, setData] = useState([]);
  const [isEmpty, setIsEmpty] = useState(true);
  const [xRange, setXRange] = useState(undefined);

  return (
    <CardTemplate title={cardTitle} subtitle={cardSubtitle}>
      <Box sx={{
        width: "100%",
        height: 280,
      }}>
        <Plot
          data={data}
          layout={{
            autosize: true,
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            font: { family: "Roboto, Helvetica, Arial, sans-serif", size: 11, color: colors.text },
            bargap: 0,
            hoverlabel: {
              bgcolor: colors.hoverBg,
              bordercolor: colors.hoverBorder,
              font: { color: colors.hoverText, size: 12 },
            },
            xaxis: {
              type: "date",
              range: xRange,
              // One tick per bin, placed mid-bar so the label sits under its bar
              ...(xTickFormat ? { dtick: binMs, tick0: new Date(binMs / 2).toISOString().slice(0, 19).replace("T", " "), tickformat: xTickFormat } : {}),
              showline: false,
              showgrid: false,
              zeroline: false,
              ticks: "",
            },
            yaxis: {
              title: { text: "Energy (kWh)", font: { size: 12, color: colors.text }, standoff: 8 },
              rangemode: "tozero", // Energy is never negative, keep axis starting at 0
              range: isEmpty ? [0, 1] : undefined, // Placeholder scale for the empty grid
              showline: false,
              showgrid: true,
              gridcolor: colors.grid,
              zeroline: true,
              zerolinecolor: colors.zeroLine,
              automargin: true,
            },
            yaxis2: {
              title: { text: "Peak power (kW)", font: { size: 12, color: colors.text }, standoff: 8 },
              range: isEmpty ? [0, 1] : undefined,
              rangemode: "tozero",
              zeroline: false, // Avoid a second zero line when the two axes scale differently
              overlaying: "y",
              side: "right",
              showline: false,
              showgrid: false,
              automargin: true,
            },
            margin: { l: 8, r: 8, t: 8, b: 32 },
            legend: {
              orientation: "h",
              x: 0,
              y: 1.02,
              xanchor: "left",
              yanchor: "bottom",
              bgcolor: "rgba(0,0,0,0)",
              font: { size: 12, color: colors.legend },
            },
          }}
          config={{
            displayModeBar: false, // Disable toolbar
          }}
          useResizeHandler={true} // Enable resizing
          style={{ width: "100%", height: "100%" }} // Auto-size to parent
        />
      </Box>
    </CardTemplate>
  )
}

export default PlotCard;